import { LocationTypeCode } from '@quromedical/fhir-common';
import {
  Location,
  UpdateHospitalSettingsSchema,
  UserUpdateHospitalSettingsRequest,
} from '@quromedical/models';
import { Col, Row } from 'components/base';
import { CrudForm, SubmissionHandler } from 'components/form';
import { Field } from 'components/types';
import { AsyncFetcher, Button, FormCard, RowConfig, Text } from 'design-system';
import { Section } from 'design-system/layout';
import { useUserSession } from 'hooks';
import { useLogout } from 'hooks/useLogout';
import { LocationApi } from 'integration/aggregate';
import React, { useCallback } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { PersonContactForm, PersonGeneralForm } from 'screens/person';
import { strings } from 'strings';
import { trpc } from 'trpc';

import { PreferenceCard } from './cards';

const createRows = (groupNames: string[] = []): RowConfig[] => [
  {
    icon: 'people-outline',
    fields: [
      {
        type: 'list',
        label: strings.GroupsFormLabel,
        display: groupNames,
      },
    ],
  },
];

const api = new LocationApi();
const fetcher: AsyncFetcher<string, Location> = async () => {
  const { locations } = await api.getAllLocations({
    type: LocationTypeCode.VirtualWard,
    status: 'active',
  });

  return locations.map(location => ({
    display: location.name || strings.ResourceNameMissing,
    value: location.id as string,
    isDisabled: false,
    meta: location as Location,
  }));
};

const wardFields: Field<Partial<UserUpdateHospitalSettingsRequest>>[] = [
  {
    heading: strings.SelectedWards,
    subfields: [
      {
        key: 'wards',
        label: 'Wards',
        type: 'combobox-multiple',
        searchable: true,
        fetcher,
      },
    ],
  },
];

export const UserProfileScreen: React.FC = () => {
  const logout = useLogout();

  const session = useUserSession();
  const profile = session.getProfile();
  const { mutate: saveHospitalSettings } =
    trpc.context.practitioner.saveHospitalSettings.useMutation();

  const groupNames = profile?.groups.map(group => group.display || strings.ResourceNameMissing);
  const groupRows = createRows(groupNames);

  const onSubmit = useCallback<SubmissionHandler<UserUpdateHospitalSettingsRequest>>(
    async a => {
      saveHospitalSettings(a, {});

      return {};
    },
    [saveHospitalSettings]
  );

  return (
    <ScrollView>
      <Section>
        <Row justifyContent="space-between">
          <Col>
            <Text variant="page-heading">{strings.UserProfileCardTitle}</Text>
          </Col>
          <Col>
            <Button text={strings.ButtonTextSignOut} onPress={logout} />
          </Col>
        </Row>
      </Section>

      <Section>
        <PreferenceCard />
      </Section>

      <Section unsetZIndex>
        <CrudForm
          fields={wardFields}
          initialValues={{}}
          buttonText="Save Changes"
          title={strings.HospitalSettings}
          onSubmit={onSubmit}
          validationSchema={UpdateHospitalSettingsSchema}
        />
      </Section>

      {profile ? (
        <>
          <Section>
            <PersonGeneralForm canEdit={false} data={profile.practitioner.general} />
          </Section>
          <Section>
            <PersonContactForm
              canEdit={false}
              data={profile.practitioner.contact}
              hasEmergencyAccess={false}
            />
          </Section>
        </>
      ) : null}

      <Section hasBottomMargin>
        <FormCard title={strings.GroupsFormLabel} showIcons rows={groupRows} />
      </Section>
    </ScrollView>
  );
};
